package one.healthium.insights.ui.shopping_list

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import one.healthium.insights.domain.models.ShoppingListModel
import one.healthium.insights.ui.shopping_list.components.ShoppingHeaderItem
import one.healthium.insights.ui.shopping_list.components.ShoppingItem
import one.healthium.insights.ui.shopping_list.models.CategoryUiModel
import one.healthium.insights.ui.shopping_list.models.HeaderUiModel
import one.healthium.insights.ui.shopping_list.models.ShoppingItemUiModel
import one.healthium.loco.resolve
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTopBar
import org.koin.compose.viewmodel.koinViewModel

interface ShoppingListEventDelegate {
    fun onBackClicked()
}

@Composable
fun ShoppingListScreen(
    model: ShoppingListModel,
    eventDelegate: ShoppingListEventDelegate,
) {
    val viewModel = koinViewModel<ShoppingListViewModel>()
    val state by viewModel.state.collectAsState()

    LaunchedEffect(model) {
        viewModel.setShoppingList(model)
    }

    ShoppingListScreenContent(
        state = state,
        onBackClicked = eventDelegate::onBackClicked,
        onShoppingItemClicked = viewModel::onShoppingItemClicked,
        onCategoryClicked = viewModel::onCategoryClicked,
        onUncheckAllClicked = viewModel::onUncheckAllClicked,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
private fun ShoppingListScreenContent(
    state: ShoppingListUiState,
    onBackClicked: () -> Unit,
    onShoppingItemClicked: (ShoppingItemUiModel) -> Unit,
    onCategoryClicked: (CategoryUiModel) -> Unit,
    onUncheckAllClicked: (HeaderUiModel) -> Unit,
) {
    AppScaffold(
        topBar = {
            AppTopBar(
                title = {
                    Text(state.title.resolve())
                },
                navigationIcon = {
                    AppBackNavigationButton(
                        onClick = onBackClicked,
                    )
                }
            )
        }
    ) {
        Box(modifier = Modifier.padding(it)) {
            ShoppingList(
                uiData = state.uiData,
                onShoppingItemClicked = onShoppingItemClicked,
                onCategoryClicked = onCategoryClicked,
                onUncheckAllClicked = onUncheckAllClicked,
            )

            AppLoadingScreen(visible = state.loading)
        }
    }
}

@Composable
private fun ShoppingList(
    uiData: List<Any>?,
    onShoppingItemClicked: (ShoppingItemUiModel) -> Unit,
    onCategoryClicked: (CategoryUiModel) -> Unit,
    onUncheckAllClicked: (HeaderUiModel) -> Unit,
) {
    if (uiData == null) return

    LazyColumn(
        contentPadding = PaddingValues(top = 12.dp, bottom = 160.dp)
    ) {
        uiData.fastForEach { item ->
            when (item) {
                is HeaderUiModel -> {
                    stickyHeader(key = item.id) {
                        ShoppingHeaderItem(
                            modifier = Modifier.animateItem(),
                            uiModel = item,
                            onCategoryClicked = onCategoryClicked,
                            onUncheckAllClicked = onUncheckAllClicked,
                        )
                    }
                }

                is ShoppingItemUiModel -> {
                    item(key = item.id) {
                        ShoppingItem(
                            modifier = Modifier.animateItem(),
                            uiModel = item,
                            onClick = {
                                onShoppingItemClicked(item)
                            },
                        )
                        Spacer(Modifier.height(12.dp))
                    }
                }

                else -> Unit
            }
        }
    }
}
